import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, ShieldCheck, FileText, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import BrandLogo from "@/components/BrandLogo";

// Admin Walk-Through Correction — Landing / front-door page.
// Public-facing but intentionally restrained: this is an internal alpha platform.
// No public pricing, no public registration, no Industry Access entry point.
export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const enterLabel = user ? "Enter Operating Dashboard" : "Enter Internal Alpha";
  const enterPath = user ? "/dashboard" : "/login";

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background:
          "radial-gradient(1200px 600px at 80% -10%, hsl(40 48% 92% / 0.55), transparent 60%), radial-gradient(900px 500px at -10% 110%, hsl(347 72% 22% / 0.10), transparent 55%), hsl(40 30% 96%)",
      }}
      data-testid="landing-page"
    >
      <header className="max-w-6xl mx-auto px-6 sm:px-8 pt-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Header brand mark — use the original red-on-black Kandake here on
              the light landing surface where it reads with high contrast. */}
          <BrandLogo variant="kandake" size="md" withRing testId="landing-header-kandake-logo" />
          <div className="min-w-0">
            <div className="font-display text-lg leading-tight">Kandake Prime</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Internal Alpha · Private
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] text-muted-foreground">
          <Lock className="w-3.5 h-3.5" /> Authorised access only
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 pt-10 sm:pt-16 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Hero copy */}
          <section className="lg:col-span-7" data-testid="landing-hero">
            <BrandLogo
              variant="kandake"
              size="xl"
              withRing
              testId="landing-hero-kandake-logo"
              className="mb-6"
            />
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] mb-5"
              style={{
                background: "hsl(40 48% 92%)",
                color: "hsl(347 72% 22%)",
                border: "1px solid hsl(40 48% 78%)",
              }}
              data-testid="landing-badge"
            >
              <span style={{ width: 6, height: 6, borderRadius: 999, background: "hsl(347 72% 22%)" }} />
              Internal Alpha · Invitation only
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Catalog rights & sync-readiness,
              <span className="block" style={{ color: "hsl(347 72% 22%)" }}>
                operated with discipline.
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Kandake Prime is the private operations platform that gates every track through manual
              compliance review before it can be marked Client-Ready or Corporate-Ready. License
              requests, submissions, document tracking, and audit trails are managed in one strict
              internal workspace — built for the team, not the public.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="landing-ctas">
              <Button
                size="lg"
                onClick={() => navigate(enterPath)}
                data-testid="landing-enter-button"
                style={{ background: "hsl(347 72% 22%)", color: "white" }}
                className="font-medium"
              >
                {enterLabel} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById("operating-authority")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="landing-learn-button"
              >
                Operating authority
              </Button>
            </div>

            <div className="mt-7 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              No public registration · No public licensing portal · Industry Access is parked for a future phase
            </div>
          </section>

          {/* Authority card */}
          <aside
            className="lg:col-span-5"
            id="operating-authority"
            data-testid="landing-authority-card"
          >
            <div
              className="rounded-xl p-6 sm:p-7 shadow-sm"
              style={{
                background: "white",
                border: "1px solid hsl(40 30% 88%)",
              }}
            >
              <div className="flex items-start gap-4">
                <BrandLogo variant="gfmp" size="lg" withRing testId="landing-authority-gfmp-logo" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Operating authority</div>
                  <div className="font-display text-2xl mt-1 leading-tight">
                    The Durban 49 Group<br />
                    <span className="text-muted-foreground text-base font-sans uppercase tracking-[0.18em]">
                      GOOD FRIAR MUSIC PUBLISHING
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                GFMP serves as the operating and administrative authority for the Kandake Prime
                platform. All compliance decisions, clearance approvals, and license workflows are
                executed by authorised internal personnel under GFMP oversight.
              </p>

              <ul className="mt-5 space-y-3 text-sm">
                <Pillar icon={<ShieldCheck className="w-4 h-4" />} title="Compliance-gated readiness">
                  Tracks must pass manual review before becoming Client-Ready or Corporate-Ready.
                </Pillar>
                <Pillar icon={<FileText className="w-4 h-4" />} title="Document & rights tracking">
                  Per-track document checklists, PRO/CMO registry, and full audit history.
                </Pillar>
                <Pillar icon={<Send className="w-4 h-4" />} title="Submission discipline">
                  Submissions cannot be sent unless the linked track is verifiably ready.
                </Pillar>
              </ul>

              <div className="mt-6 pt-5 border-t text-[11px] text-muted-foreground leading-relaxed" data-testid="landing-disclaimer">
                Internal-use only. This platform does not provide public licensing, payments,
                marketplace functionality, or legal guarantees. Access is provisioned by GFMP /
                Super Admin.
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-muted-foreground">
          <div>
            © {new Date().getFullYear()} The Durban 49 Group · GOOD FRIAR MUSIC PUBLISHING. Internal use only.
          </div>
          <div className="inline-flex items-center gap-2" data-testid="landing-powered-by">
            <BrandLogo variant="kandake" size="xs" ariaHidden />
            <span>Powered by <span className="font-medium" style={{ color: "hsl(347 72% 22%)" }}>Kandake</span></span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Pillar({ icon, title, children }) {
  return (
    <li className="flex gap-3">
      <span
        className="mt-0.5 inline-grid place-items-center w-7 h-7 rounded-md shrink-0"
        style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)" }}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="font-medium text-foreground">{title}</span>
        <span className="block text-muted-foreground">{children}</span>
      </span>
    </li>
  );
}
